import { SceneManager } from './scene/SceneManager';
import { WeatherService } from './weather/WeatherService';
import { SimulationState, NacelleComponentInfo } from './types';
import { NACELLE_COMPONENTS } from './turbine/Drivetrain';

// Initial state
const state: SimulationState = {
  waveHeight: 1.2,
  wavePeriod: 5.5,
  waveDirection: 45,
  wireframe: false,
  meshResolution: 'medium',

  windSpeed: 8.5,
  windDirection: 35,
  isPlaying: true,
  turbineSpeedMultiplier: 1.0,

  timeOfDay: 12.0,
  autoTimeProgress: false,
  useSunCalc: false,

  dataSourceMode: 'manual',

  isNacelleInspecting: false,
  selectedComponentId: 'gearbox'
};

// Compass heading helper
function getCompassHeading(degrees: number): string {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(((degrees % 360) + 360) % 360 / 22.5) % 16;
  return directions[index];
}

// Format time string
function formatHoursToTime(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.floor((hours - h) * 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

// Initialize Application
window.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('canvas-container');
  if (!container) return;

  const sceneManager = new SceneManager(container);
  const weatherService = new WeatherService();

  // Cache DOM Elements
  const leftPanel = document.getElementById('controls-panel');
  const rightPanel = document.getElementById('data-panel');
  const leftToggleBtn = document.getElementById('left-toggle-btn');
  const rightToggleBtn = document.getElementById('right-toggle-btn');

  const waveHeightSlider = document.getElementById('wave-height-slider') as HTMLInputElement;
  const wavePeriodSlider = document.getElementById('wave-period-slider') as HTMLInputElement;
  const waveDirSlider = document.getElementById('wave-dir-slider') as HTMLInputElement;
  const valWaveHeight = document.getElementById('val-wave-height');
  const valWavePeriod = document.getElementById('val-wave-period');
  const valWaveDir = document.getElementById('val-wave-dir');
  const valWaveDirCompass = document.getElementById('val-wave-dir-compass');
  const toggleWireframe = document.getElementById('toggle-wireframe') as HTMLInputElement;
  const btnResetWave = document.getElementById('btn-reset-wave');
  const waveModeBadge = document.getElementById('wave-mode-badge');

  const btnTogglePlay = document.getElementById('btn-toggle-play');
  const playBtnText = document.getElementById('play-btn-text');
  const windSpeedSlider = document.getElementById('wind-speed-slider') as HTMLInputElement;
  const windDirSlider = document.getElementById('wind-dir-slider') as HTMLInputElement;
  const valWindSpeed = document.getElementById('val-wind-speed');
  const valWindDir = document.getElementById('val-wind-dir');
  const valWindDirCompass = document.getElementById('val-wind-dir-compass');

  const timeOfDaySlider = document.getElementById('time-of-day-slider') as HTMLInputElement;
  const valTimeOfDay = document.getElementById('val-time-of-day');
  const toggleSuncalc = document.getElementById('toggle-suncalc') as HTMLInputElement;
  const sunModeBadge = document.getElementById('sun-mode-badge');
  const suncalcDesc = document.getElementById('suncalc-desc');

  const modeManualBtn = document.getElementById('mode-manual-btn');
  const modeRealtimeBtn = document.getElementById('mode-realtime-btn');

  const btnInspectNacelle = document.getElementById('btn-inspect-nacelle');
  const btnCloseNacelle = document.getElementById('btn-close-nacelle');
  const nacelleModal = document.getElementById('nacelle-modal');
  const btnResetView = document.getElementById('btn-reset-view');

  const compName = document.getElementById('nacelle-comp-name');
  const compEn = document.getElementById('nacelle-comp-en');
  const compRoleTag = document.getElementById('comp-role-tag');
  const compSpecTag = document.getElementById('comp-spec-tag');
  const compDescription = document.getElementById('comp-description');

  const botWindDir = document.getElementById('bot-wind-dir');
  const botWaveDir = document.getElementById('bot-wave-dir');

  // Weather & Marine display elements
  const dispWeatherGrid = document.getElementById('disp-weather-grid');
  const dispMarineGrid = document.getElementById('disp-marine-grid');
  const dispDataStatus = document.getElementById('disp-data-status');
  const dispWeatherTime = document.getElementById('disp-weather-time');
  const dispMarineTime = document.getElementById('disp-marine-time');
  const dispWindSpeed = document.getElementById('disp-wind-speed');
  const dispWindDir = document.getElementById('disp-wind-dir');
  const dispWindCompass = document.getElementById('disp-wind-compass');
  const dispWaveHeight = document.getElementById('disp-wave-height');
  const dispWavePeriod = document.getElementById('disp-wave-period');
  const dispWaveDir = document.getElementById('disp-wave-dir');
  const btnRefreshData = document.getElementById('btn-refresh-data');

  // 1. Panel Collapsible Toggles
  leftToggleBtn?.addEventListener('click', () => {
    leftPanel?.classList.toggle('collapsed');
  });

  rightToggleBtn?.addEventListener('click', () => {
    rightPanel?.classList.toggle('collapsed');
  });

  // 2. Camera Toolbar Presets
  const presetButtons = document.querySelectorAll<HTMLButtonElement>('.camera-toolbar .tool-btn[data-preset]');
  presetButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const preset = btn.getAttribute('data-preset') as any;
      if (preset) {
        presetButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        sceneManager.setCameraPreset(preset);

        if (preset !== 'nacelle' && state.isNacelleInspecting) {
          closeNacelleInspection();
        }
      }
    });
  });

  btnResetView?.addEventListener('click', () => {
    presetButtons.forEach(b => b.classList.remove('active'));
    document.querySelector('.camera-toolbar .tool-btn[data-preset="overview"]')?.classList.add('active');
    sceneManager.resetView();
    if (state.isNacelleInspecting) {
      closeNacelleInspection();
    }
  });

  // 3. Nacelle Inspection Functions
  function openNacelleInspection(initialCompId: string = 'gearbox') {
    state.isNacelleInspecting = true;
    nacelleModal?.classList.add('active');
    sceneManager.turbine.setShellOpen(true);
    sceneManager.setCameraPreset('nacelle');

    presetButtons.forEach(b => b.classList.remove('active'));
    btnInspectNacelle?.classList.add('active');

    selectNacelleComponent(initialCompId);
  }

  function closeNacelleInspection() {
    state.isNacelleInspecting = false;
    nacelleModal?.classList.remove('active');
    sceneManager.turbine.setShellOpen(false);
    sceneManager.turbine.drivetrain.highlightComponent(null);
    btnInspectNacelle?.classList.remove('active');
    document.querySelector('.camera-toolbar .tool-btn[data-preset="turbine"]')?.classList.add('active');
    sceneManager.setCameraPreset('turbine');
  }

  function selectNacelleComponent(compId: string) {
    state.selectedComponentId = compId;
    const info = NACELLE_COMPONENTS[compId];
    if (info) {
      if (compName) compName.textContent = info.name;
      if (compEn) compEn.textContent = info.nameEn;
      if (compRoleTag) compRoleTag.textContent = `核心功能：${info.role}`;
      if (compSpecTag) compSpecTag.textContent = `運轉規格：${info.specs}`;
      if (compDescription) compDescription.textContent = `${info.details} [能量流向: ${info.energyFlow}]`;

      // Highlight in 3D
      sceneManager.turbine.drivetrain.highlightComponent(compId);

      // Highlight chip buttons
      document.querySelectorAll('.quick-comp-chips .chip').forEach(c => {
        if (c.getAttribute('data-comp') === compId) {
          c.classList.add('active');
        } else {
          c.classList.remove('active');
        }
      });

      // Highlight flow step nodes
      document.querySelectorAll('.flow-node[data-target]').forEach(n => {
        if (n.getAttribute('data-target') === compId) {
          n.classList.add('active');
        } else {
          n.classList.remove('active');
        }
      });
    }
  }

  btnInspectNacelle?.addEventListener('click', () => {
    if (state.isNacelleInspecting) {
      closeNacelleInspection();
    } else {
      openNacelleInspection();
    }
  });

  btnCloseNacelle?.addEventListener('click', () => {
    closeNacelleInspection();
  });

  // Scene click callbacks
  sceneManager.onNacelleClicked = () => {
    if (!state.isNacelleInspecting) {
      openNacelleInspection();
    }
  };

  sceneManager.onComponentSelected = (info: NacelleComponentInfo | null) => {
    if (info) {
      selectNacelleComponent(info.id);
    }
  };

  // Component chips in modal
  document.querySelectorAll<HTMLButtonElement>('.quick-comp-chips .chip').forEach(chip => {
    chip.addEventListener('click', () => {
      const id = chip.getAttribute('data-comp');
      if (id) selectNacelleComponent(id);
    });
  });

  // Flow step nodes in modal
  document.querySelectorAll<HTMLElement>('.flow-node[data-target]').forEach(node => {
    node.addEventListener('click', () => {
      const id = node.getAttribute('data-target');
      if (id) selectNacelleComponent(id);
    });
  });

  // 4. Wave & Sea Surface Controls
  function applyWaveParameters() {
    sceneManager.ocean.setWaveHeight(state.waveHeight);
    sceneManager.ocean.setWavePeriod(state.wavePeriod);
    sceneManager.ocean.setWaveDirection(state.waveDirection);

    if (valWaveHeight) valWaveHeight.textContent = state.waveHeight.toFixed(1);
    if (valWavePeriod) valWavePeriod.textContent = state.wavePeriod.toFixed(1);
    if (valWaveDir) valWaveDir.textContent = Math.round(state.waveDirection).toString();
    if (valWaveDirCompass) valWaveDirCompass.textContent = getCompassHeading(state.waveDirection);
    if (botWaveDir) botWaveDir.textContent = `${Math.round(state.waveDirection)}°`;
  }

  waveHeightSlider?.addEventListener('input', () => {
    if (state.dataSourceMode === 'manual') {
      state.waveHeight = parseFloat(waveHeightSlider.value);
      applyWaveParameters();
    }
  });

  wavePeriodSlider?.addEventListener('input', () => {
    if (state.dataSourceMode === 'manual') {
      state.wavePeriod = parseFloat(wavePeriodSlider.value);
      applyWaveParameters();
    }
  });

  waveDirSlider?.addEventListener('input', () => {
    if (state.dataSourceMode === 'manual') {
      state.waveDirection = parseFloat(waveDirSlider.value);
      applyWaveParameters();
    }
  });

  toggleWireframe?.addEventListener('change', () => {
    state.wireframe = toggleWireframe.checked;
    sceneManager.ocean.setWireframe(state.wireframe);
  });

  // Mesh resolution buttons
  const resButtons = document.querySelectorAll<HTMLButtonElement>('.segmented-control [data-res]');
  resButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const res = btn.getAttribute('data-res') as 'low' | 'medium' | 'high';
      if (res) {
        resButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        state.meshResolution = res;
        sceneManager.ocean.setResolution(res);
      }
    });
  });

  btnResetWave?.addEventListener('click', () => {
    state.waveHeight = 1.2;
    state.wavePeriod = 5.5;
    state.waveDirection = 45;
    if (waveHeightSlider) waveHeightSlider.value = '1.2';
    if (wavePeriodSlider) wavePeriodSlider.value = '5.5';
    if (waveDirSlider) waveDirSlider.value = '45';
    applyWaveParameters();
  });

  // 5. Turbine Controls
  function applyTurbineParameters() {
    sceneManager.turbine.setWindSpeed(state.windSpeed);
    sceneManager.turbine.setYawDirection(state.windDirection);

    if (valWindSpeed) valWindSpeed.textContent = state.windSpeed.toFixed(1);
    if (valWindDir) valWindDir.textContent = Math.round(state.windDirection).toString();
    if (valWindDirCompass) valWindDirCompass.textContent = getCompassHeading(state.windDirection);
    if (botWindDir) botWindDir.textContent = `${Math.round(state.windDirection)}°`;
  }

  windSpeedSlider?.addEventListener('input', () => {
    if (state.dataSourceMode === 'manual') {
      state.windSpeed = parseFloat(windSpeedSlider.value);
      applyTurbineParameters();
    }
  });

  windDirSlider?.addEventListener('input', () => {
    if (state.dataSourceMode === 'manual') {
      state.windDirection = parseFloat(windDirSlider.value);
      applyTurbineParameters();
    }
  });

  btnTogglePlay?.addEventListener('click', () => {
    state.isPlaying = !state.isPlaying;
    sceneManager.turbine.setPlaying(state.isPlaying);
    if (playBtnText) {
      playBtnText.textContent = state.isPlaying ? '暫停旋轉' : '啟動旋轉';
    }
    btnTogglePlay.classList.toggle('btn-primary', state.isPlaying);
    btnTogglePlay.classList.toggle('btn-secondary', !state.isPlaying);
  });

  // 6. Day/Night & Lighting
  function updateLightingFromState() {
    sceneManager.lightingManager.updateTime(state.timeOfDay, state.useSunCalc);
    sceneManager.updateOceanLighting();

    if (valTimeOfDay) {
      valTimeOfDay.textContent = formatHoursToTime(state.timeOfDay);
    }
  }

  timeOfDaySlider?.addEventListener('input', () => {
    state.timeOfDay = parseFloat(timeOfDaySlider.value);
    updateLightingFromState();
  });

  toggleSuncalc?.addEventListener('change', () => {
    state.useSunCalc = toggleSuncalc.checked;
    if (sunModeBadge) {
      sunModeBadge.textContent = state.useSunCalc ? 'SunCalc 天文' : '示意日夜';
      sunModeBadge.classList.toggle('yellow', state.useSunCalc);
    }
    if (suncalcDesc) {
      suncalcDesc.textContent = state.useSunCalc
        ? '已啟用 SunCalc：依彰化外海 24.1°N, 120.1°E 即時太陽與月球仰角及方位角精確換算 3D 光源座標。'
        : '目前為教學示意日夜；光源按標準 24 小時連續軌道平滑移動。';
    }
    updateLightingFromState();
  });

  // 7. Mode Switcher (Manual vs Realtime Data)
  function setMode(mode: 'manual' | 'realtime') {
    state.dataSourceMode = mode;
    modeManualBtn?.classList.toggle('active', mode === 'manual');
    modeRealtimeBtn?.classList.toggle('active', mode === 'realtime');

    if (waveModeBadge) {
      waveModeBadge.textContent = mode === 'realtime' ? '即時預報連動' : '教學示意';
      waveModeBadge.classList.toggle('badge', true);
      waveModeBadge.classList.toggle('yellow', mode === 'realtime');
    }

    // Disable manual sliders in realtime mode
    const isManual = mode === 'manual';
    waveHeightSlider.disabled = !isManual;
    wavePeriodSlider.disabled = !isManual;
    waveDirSlider.disabled = !isManual;
    windSpeedSlider.disabled = !isManual;
    windDirSlider.disabled = !isManual;

    if (mode === 'realtime') {
      applyLiveApiData();
    } else {
      applyWaveParameters();
      applyTurbineParameters();
    }
  }

  modeManualBtn?.addEventListener('click', () => setMode('manual'));
  modeRealtimeBtn?.addEventListener('click', () => setMode('realtime'));

  function applyLiveApiData() {
    const weather = weatherService.getWeatherData();
    const marine = weatherService.getMarineData();

    if (weather && weather.status === 'success') {
      state.windSpeed = weather.windSpeed10m;
      state.windDirection = weather.windDirection10m;
      if (windSpeedSlider) windSpeedSlider.value = weather.windSpeed10m.toString();
      if (windDirSlider) windDirSlider.value = weather.windDirection10m.toString();
      applyTurbineParameters();
    }

    if (marine && marine.status === 'success') {
      state.waveHeight = marine.waveHeight;
      state.wavePeriod = marine.wavePeriod;
      state.waveDirection = marine.waveDirection;
      if (waveHeightSlider) waveHeightSlider.value = marine.waveHeight.toString();
      if (wavePeriodSlider) wavePeriodSlider.value = marine.wavePeriod.toString();
      if (waveDirSlider) waveDirSlider.value = marine.waveDirection.toString();
      applyWaveParameters();
    }
  }

  // 8. Weather & Marine Data Update Listeners
  weatherService.addListener((weather, marine) => {
    if (weather) {
      if (dispWeatherGrid) dispWeatherGrid.textContent = `${weather.returnedCoord.latitude.toFixed(4)}°N, ${weather.returnedCoord.longitude.toFixed(4)}°E`;
      if (dispWeatherTime) dispWeatherTime.textContent = weather.time;
      if (dispWindSpeed) dispWindSpeed.textContent = weather.status === 'success' ? weather.windSpeed10m.toFixed(1) : '--';
      if (dispWindDir) dispWindDir.textContent = weather.status === 'success' ? Math.round(weather.windDirection10m).toString() : '--';
      if (dispWindCompass) dispWindCompass.textContent = weather.status === 'success' ? getCompassHeading(weather.windDirection10m) : '--';
    }

    if (marine) {
      if (dispMarineGrid) dispMarineGrid.textContent = `${marine.returnedCoord.latitude.toFixed(4)}°N, ${marine.returnedCoord.longitude.toFixed(4)}°E`;
      if (dispMarineTime) dispMarineTime.textContent = marine.time;
      if (dispWaveHeight) dispWaveHeight.textContent = marine.status === 'success' ? marine.waveHeight.toFixed(2) : '--';
      if (dispWavePeriod) dispWavePeriod.textContent = marine.status === 'success' ? marine.wavePeriod.toFixed(1) : '--';
      if (dispWaveDir) dispWaveDir.textContent = marine.status === 'success' ? Math.round(marine.waveDirection).toString() : '--';
    }

    if (dispDataStatus) {
      if (weather?.status === 'loading' || marine?.status === 'loading') {
        dispDataStatus.className = 'status-pill loading';
        dispDataStatus.textContent = '資料連線中...';
      } else if (weather?.status === 'success' && marine?.status === 'success') {
        dispDataStatus.className = 'status-pill success';
        dispDataStatus.textContent = '最新預報已連線';
      } else {
        dispDataStatus.className = 'status-pill error';
        dispDataStatus.textContent = '部分資料讀取異常';
      }
    }

    if (state.dataSourceMode === 'realtime') {
      applyLiveApiData();
    }
  });

  btnRefreshData?.addEventListener('click', () => {
    weatherService.fetchAll();
  });

  // Fetch initial live data
  weatherService.fetchAll();

  // Initial application setup
  applyWaveParameters();
  applyTurbineParameters();
  updateLightingFromState();

  // 9. Animation & Render Loop
  function animate() {
    requestAnimationFrame(animate);
    sceneManager.render(state);
  }

  animate();
});
